import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { snakeCase } from 'change-object-case'
// Services
import { getCustomers } from 'services/customers'
import useCustomerModals from 'components/CustomerModals/useCustomerModals'
import debounce from 'utils/debounce'
import { useNotification } from 'components/Notification'
import { CUSTOMER_SORT_KEY } from 'constants/localStorage'

const defaultParams = {
  itemCount: 0,
  pageSize: 50,
  pageOffset: 0,
  page: 0,
}

const defaultFilters = {
  state: 'active',
  type: null,
  term: null,
}

const debouncedSearch = debounce(getCustomers, 150)

export const CUSTOMER_ACCOUNT_QUERY_KEY = 'customerAccountsQuery'

// The chosen order is kept under a key this page owns rather than read back out of the
// grid's own persisted state. The component library rewrites that whole blob from the
// live grid whenever a column is resized, reordered or pinned or the page size changes,
// and the live grid is unsorted while a restored order is being withheld during a
// search, so the stored order would be erased with no signal to the user.
// `isExplicit` records whether the order came from a click in this session or from
// storage, which decides whether it is allowed to outrank a search's relevance.
const restoreSort = () => {
  const stored = JSON.parse(localStorage.getItem(CUSTOMER_SORT_KEY) || 'null')

  return stored?.field && stored?.direction
    ? { field: stored.field, direction: stored.direction, isExplicit: false }
    : null
}

function useCustomers() {
  const { createNotification } = useNotification()
  const queryClient = useQueryClient()
  const [pagination, setPagination] = useState(defaultParams)
  const [filters, setFilters] = useState(defaultFilters)
  // `null` means there is no order to apply, so no `sort` param is sent and the API's
  // own default takes over: name ascending when browsing, best-match first when a
  // search term is present.
  const [sort, setSort] = useState(restoreSort)

  // A restored order is dropped while a search term is active, because the user never
  // asked for it here and it would outrank the similarity score and turn the results
  // alphabetical. An order picked in this session always wins.
  const activeSort = sort && (sort.isExplicit || !filters.term) ? sort : null

  const {
    getCustomerModals,
    handleCustomerFeatureToggle,
    handleCustomerEdit,
    handleCustomerSecurity,
    defaultHandleCustomerSave,
    defaultHandleCustomerArchive,
    defaultHandleCustomerUnarchive,
    defaultHandleFeatureToggleUpdate,
  } = useCustomerModals()

  const params = {
    state: filters.state,
    type: filters.type,
    term: filters.term,
    pageSize: pagination.pageSize,
    pageOffset: pagination.pageOffset,
    // `field` is the DataGrid column field (camelCase); the API expects `<field>_<direction>`.
    ...(activeSort && { sort: `${snakeCase(activeSort.field)}_${activeSort.direction}` }),
  }

  const queryKey = [CUSTOMER_ACCOUNT_QUERY_KEY, params]

  const resetCustomerAccountsQuery = () =>
    queryClient.resetQueries({
      queryKey,
      exact: true,
      onError: error =>
        createNotification({
          message: `Error in fetching customer accounts, ${error.message}`,
          variant: 'error',
        }),
    })

  const { data, isFetching: isLoading } = useQuery({
    queryKey,
    queryFn: () => debouncedSearch(params),
    placeholderData: { data: [], meta: defaultParams },
    select: data => ({ customers: data.data, meta: { ...data.meta, page: pagination.page } }),
    refetchOnWindowFocus: false,
    throwOnError: true,
  })

  const handleCustomerTypeFilterChange = customerType => {
    const type = customerType === 'all' ? null : customerType

    setFilters(prevState => ({ ...prevState, type }))
  }

  const handleCustomerNameSearch = e => {
    const term = e.target.value || null
    setFilters(prevState => ({ ...prevState, term }))
  }

  const handleCustomerActiveStateFilterChange = state => {
    setFilters(prevState => ({ ...prevState, state }))
  }

  const handleSortChange = (field, direction) => {
    setSort({ field, direction, isExplicit: true })
    localStorage.setItem(CUSTOMER_SORT_KEY, JSON.stringify({ field, direction }))
    // A new ordering makes the current offset meaningless, so go back to the first page.
    setPagination(prevState => ({ ...prevState, page: 0, pageOffset: 0 }))
  }

  // The grid's Reset button clears the library's own stored state and reloads. Our key
  // is not part of that blob, so it has to be cleared here too, or a reset would leave
  // the order in place with nothing else in the UI able to clear it: the library pins
  // sortingOrder to desc/asc with no unsorted step, and drops empty sort models.
  const handleSortReset = () => {
    localStorage.removeItem(CUSTOMER_SORT_KEY)
    setSort(null)
  }

  const { mutateAsync: handleCustomerSave } = useMutation({
    mutationFn: defaultHandleCustomerSave,
    onError: (error, customerArg) => {
      // Validation errors (client errors) are shown inline in the CustomerModal form.
      const status = error.response?.status
      if (status === 400 || status === 422) {
        return
      }

      createNotification({
        message: customerArg.id
          ? `Error saving customer ${customerArg.id}, ${error.message}`
          : `Error creating new customer, ${error.message}`,
        variant: 'error',
      })
    },
    onSuccess: (updatedCustomer, customerArg) => {
      queryClient.setQueryData(queryKey, oldData => {
        const { data: oldCustomers, meta } = oldData
        const updatedCustomers = customerArg.id
          ? oldCustomers.map(oldCustomer =>
              oldCustomer.id === updatedCustomer.id ? updatedCustomer : oldCustomer,
            )
          : [updatedCustomer, ...oldCustomers]
        return { meta, data: updatedCustomers }
      })
    },
  })

  const { mutateAsync: handleCustomerArchive } = useMutation({
    mutationFn: defaultHandleCustomerArchive,
    onError: (error, customerArg) =>
      createNotification({
        message: `Error archiving customer ${customerArg.id}, ${error.message}`,
        variant: 'error',
      }),
    onSuccess: resetCustomerAccountsQuery,
  })

  const { mutateAsync: handleCustomerUnarchive } = useMutation({
    mutationFn: defaultHandleCustomerUnarchive,
    onError: (error, customerArg) =>
      createNotification({
        message: `Error unarchiving customer ${customerArg.id}, ${error.message}`,
        variant: 'error',
      }),
    onSuccess: resetCustomerAccountsQuery,
  })

  const { mutateAsync: handleFeatureToggleUpdate } = useMutation({
    mutationFn: ({ customer, features }) => defaultHandleFeatureToggleUpdate(customer, features),
    onError: (error, { customer }) =>
      createNotification({
        message: `Error updating feature toggles for customer ${customer.id}, ${error.message}`,
        variant: 'error',
      }),
    onSuccess: updatedCustomer => {
      queryClient.setQueryData(queryKey, oldData => {
        const updatedCustomers = oldData.data.map(oldCustomer =>
          oldCustomer.id === updatedCustomer.id ? updatedCustomer : oldCustomer,
        )
        return { meta: oldData.meta, data: updatedCustomers }
      })
    },
  })

  const getModalComponents = () => {
    return getCustomerModals(
      (customer, features) => handleFeatureToggleUpdate({ customer, features }),
      handleCustomerSave,
    )
  }

  return {
    customers: data.customers,
    pagination: data.meta,
    isLoading,
    filters,
    sort: activeSort,
    setPagination,
    handleSortChange,
    handleSortReset,
    handleCustomerTypeFilterChange,
    handleCustomerActiveStateFilterChange,
    handleCustomerNameSearch,
    handleCustomerEdit,
    handleCustomerFeatureToggle,
    handleCustomerArchive,
    handleCustomerUnarchive,
    handleCustomerSecurity,
    getModalComponents,
  }
}

export { useCustomers }
